import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import { useStaffAuth } from '@/features/auth/useStaffAuth';
import { CommissionDialog } from '@/features/commissioning/CommissionDialog';
import { InterlockCell } from '@/features/interlock/InterlockCell';
import { SetInterlockDialog } from '@/features/interlock/SetInterlockDialog';
import { isReadyToCommission } from '@/features/commissioning/useCommissioning';
import { AssignDeviceDialog } from '@/features/provisioning/AssignDeviceDialog';
import {
  DecommissionDeviceDialog,
  DeviceHistoryDialog,
  DeviceSimDialog,
  EditDeviceDialog,
  NewDeviceDialog,
  SwapDeviceDialog,
} from '@/features/inventory/DeviceDialogs';
import {
  Pill,
  PRIMARY_BUTTON,
  RegisterScreen,
  SECONDARY_BUTTON,
  type RegisterChip,
  type RegisterColumn,
} from '@/features/inventory/registerUi';
import {
  canDecommission,
  isAssignableToCustomer,
  useDevices,
  useSims,
  type AdminDevice,
  type DeviceStatus,
} from '@/features/inventory/useInventory';

/**
 * The device register — canvas `1u` (slice 18d).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT `1u` DRAWS THAT THIS DOES NOT BUILD, AND WHY.
 * ─────────────────────────────────────────────────────────────────────────────
 * `1u` is one table mixing devices and SIMs, with columns IMEI / Hardware /
 * Protocol·Port / SIM / Tenant / Status / Actions and four chips (Active, Stock,
 * Offline, Expiring SIMs). What is real here is the register: an IMEI, a model,
 * the fitted SIM, the vehicle it is in, and its condition.
 *
 *   - **Protocol · Port** — the protocol is a property of the INSTALL, chosen in
 *     the provisioning wizard and held by Traccar. There is no protocol column
 *     on `devices` and nothing to render.
 *   - **Tenant** — the register resolves the VEHICLE through the open
 *     assignment; `GET /admin/devices` returns `vehiclePlate`, not a tenant
 *     name. The plate is what an installer reads off the windscreen anyway.
 *   - **Offline** — needs last-position state. This screen reads the register,
 *     not Redis, and a chip that silently means something else is worse than no
 *     chip.
 *   - **Expiring SIMs** — no carrier expiry date exists on `sims`.
 *   - **Batch import CSV**, the Traccar-ingest / SIM-fleet / server-health strip
 *     and **Command / Logs** per row — no endpoint behind any of them.
 *
 * None of the above ships greyed out or "coming soon": absence over disabled.
 *
 * 🔴 THE "REMOVE FROM VEHICLE" ACTION LIVES ON `/vehicles`, NOT HERE (18g), AND
 * THIS PARAGRAPH USED TO SAY IT DID NOT EXIST AT ALL.
 *
 * It said `vehicles.traccar_device_id` is NOT NULL, so a vehicle with no tracker
 * could not be represented and there was no endpoint. Migration 0026 made that
 * column nullable deliberately and `DELETE /admin/vehicles/:id/device` exists.
 * The sentence is corrected rather than left standing — a comment describing a
 * constraint the schema no longer has is how the next reader concludes something
 * is protected when it is not.
 *
 * It is on the VEHICLE screen because the choice it demands is about the VEHICLE:
 * the removal reason decides whether a paying customer keeps seeing the truck.
 * Offering it from a hardware register, where the vehicle is one cell in a row,
 * would put that decision in the wrong context. Replace stays here.
 *
 * 🔴 DECOMMISSION IS HERE, AND IT IS THE INVERSE OF COMMISSION, NOT A DELETE.
 * It removes the box from the tracking engine so it can be commissioned again;
 * every position it ever sent is kept. Absent unless the device is commissioned
 * AND in stock — `canDecommission`, the same two preconditions the server
 * checks — because a greyed Decommission on the tracker inside a customer's
 * truck teaches nothing, while its absence beside the plate one column over is
 * the whole explanation.
 *
 * 🔴 SUPER-ADMIN ONLY, GATED HERE AND NOT ONLY IN THE NAV. Every inventory route
 * is `requireSuperAdmin`; a deep link by an `admin_staff` would render a screen
 * whose every fetch 403s, so it redirects instead.
 */

const DEVICE_TONE: Record<DeviceStatus, string> = {
  in_service: 'border-raad-border text-raad-muted',
  faulty: 'border-raad-warn/40 text-raad-warn',
  retired: 'border-raad-error/40 text-raad-error',
};

export function Devices() {
  const { t } = useTranslation();
  const { state } = useStaffAuth();
  // Read once: it gates the redirect below AND the queries, so a non-super-admin
  // neither sees the screen nor calls a route that would only 403.
  const isSuperAdmin = state.status === 'authenticated' && state.user.role === 'super_admin';
  const devicesQ = useDevices(isSuperAdmin);
  const simsQ = useSims(isSuperAdmin);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminDevice | null>(null);
  const [simFor, setSimFor] = useState<AdminDevice | null>(null);
  const [historyFor, setHistoryFor] = useState<AdminDevice | null>(null);
  const [swapFor, setSwapFor] = useState<AdminDevice | null>(null);
  const [commissioning, setCommissioning] = useState<AdminDevice | null>(null);
  const [assigning, setAssigning] = useState<AdminDevice | null>(null);
  const [decommissioning, setDecommissioning] = useState<AdminDevice | null>(null);
  const [interlockFor, setInterlockFor] = useState<AdminDevice | null>(null);

  if (state.status === 'authenticated' && !isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  if (devicesQ.isPending) return <p className="text-sm text-raad-muted">{t('inventory.loading')}</p>;

  // Defensive for the reason slice 24b earned: an unexpected response shape must
  // not throw during render and take the portal down. Treated as an ERROR, never
  // as an empty register — "no devices" would be a claim about the warehouse.
  const rows = Array.isArray(devicesQ.data?.devices) ? devicesQ.data.devices : null;
  if (devicesQ.isError || rows === null) {
    return (
      <p role="alert" className="text-sm text-raad-error">
        {t('inventory.devices.error')}
      </p>
    );
  }

  const sims = Array.isArray(simsQ.data?.sims) ? simsQ.data.sims : [];
  const installed = rows.filter((d) => !d.inStock).length;
  const inStock = rows.filter((d) => d.inStock).length;

  const columns: Array<RegisterColumn<AdminDevice>> = [
    {
      key: 'imei',
      label: t('inventory.devices.imei'),
      render: (d) => (
        <span className="num font-medium">
          {d.imei}
          {d.notes ? (
            <span className="mt-0.5 block text-[11.5px] font-normal text-raad-muted">{d.notes}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'hardware',
      label: t('inventory.devices.hardware'),
      render: (d) => (
        <>
          <span>{d.model ?? t('inventory.unknown')}</span>
          {d.firmware ? (
            <span className="mt-0.5 block text-[11.5px] text-raad-muted num">{d.firmware}</span>
          ) : null}
        </>
      ),
    },
    {
      key: 'sim',
      label: t('inventory.devices.sim'),
      render: (d) =>
        d.simId === null ? (
          <span className="text-raad-muted">{t('inventory.devices.noSim')}</span>
        ) : (
          <>
            <span className="num">{d.simIccid}</span>
            <span className="mt-0.5 block text-[11.5px] text-raad-muted">
              {d.simMsisdn === null ? (
                // Not a blank. A fitted SIM with no number never reports, and
                // that is the single most useful thing this cell can say.
                <span className="text-raad-warn">{t('inventory.sims.notActivated')}</span>
              ) : (
                <span className="num">{d.simMsisdn}</span>
              )}
              {d.simCarrier ? ` · ${d.simCarrier}` : ''}
            </span>
          </>
        ),
    },
    {
      key: 'vehicle',
      label: t('inventory.devices.vehicle'),
      render: (d) =>
        d.vehiclePlate === null ? (
          <span className="text-raad-muted">{t('inventory.inStock')}</span>
        ) : (
          <span className="num">{d.vehiclePlate}</span>
        ),
    },
    {
      key: 'status',
      label: t('inventory.status'),
      render: (d) => (
        <Pill label={t(`inventory.deviceStatus.${d.status}`)} tone={DEVICE_TONE[d.status]} />
      ),
    },
    {
      // 🔴 SLICE 14e-fix — THE INTERLOCK CONTROL MOVED HERE FROM THE BENCH.
      // The bench reads `devices_commissioned` (no open assignment), so it could
      // only ever show trackers not yet given to a customer — never an INSTALLED
      // device, which is the only kind whose interlock decides whether a real
      // engine-cut command skips the speed and freshness guards. This register
      // selects `FROM devices` and lists installed hardware too, so it is the one
      // surface where an operator can set `starter` on the vehicle they mean.
      // `InterlockCell` is presentational and handles the null "not recorded"
      // default; a header cell is emitted automatically by `RegisterScreen` from
      // this same column entry, so head and body cannot fall out of step.
      key: 'interlock',
      label: t('interlock.col.header'),
      render: (d) => (
        <InterlockCell interlockType={d.interlockType} onEdit={() => setInterlockFor(d)} />
      ),
    },
    {
      key: 'actions',
      label: t('inventory.actions'),
      render: (d) => (
        <span className="flex flex-wrap gap-2">
          <button type="button" className={SECONDARY_BUTTON} onClick={() => setEditing(d)}>
            {t('inventory.edit')}
          </button>
          <button type="button" className={SECONDARY_BUTTON} onClick={() => setSimFor(d)}>
            {d.simId === null ? t('inventory.devices.installSim') : t('inventory.devices.removeSim')}
          </button>
          {/* Replace appears ONLY for a device that is actually in a vehicle —
              there is no vehicle to re-fit otherwise. It is absent, not
              disabled, for a device in stock. */}
          {d.vehicleId !== null ? (
            <button type="button" className={SECONDARY_BUTTON} onClick={() => setSwapFor(d)}>
              {t('inventory.devices.replace')}
            </button>
          ) : null}
          {/* 🔴 SLICE 18f — COMMISSION AND ASSIGN, EACH ABSENT UNLESS IT APPLIES.
              Never disabled: a greyed "Commission" on a tracker with no SIM
              teaches nothing, while its absence plus the "no SIM" cell one column
              over is the whole explanation. The two predicates are mutually
              exclusive by construction (`traccarDeviceId` null vs not), so a row
              never offers both. */}
          {isReadyToCommission({
            traccarDeviceId: d.traccarDeviceId,
            status: d.status,
            inStock: d.inStock,
            simId: d.simId,
          }) ? (
            <button type="button" className={SECONDARY_BUTTON} onClick={() => setCommissioning(d)}>
              {t('commissioning.commission')}
            </button>
          ) : null}
          {isAssignableToCustomer(d) ? (
            <button type="button" className={SECONDARY_BUTTON} onClick={() => setAssigning(d)}>
              {t('commissioning.assign')}
            </button>
          ) : null}
          {/* 🔴 SLICE 18g — THE INVERSE OF COMMISSION. Absent, never disabled:
              the two preconditions are visible on the row itself (the vehicle
              cell says whether it is fitted), so the absence explains itself
              where the operator already is. */}
          {canDecommission(d) ? (
            <button
              type="button"
              className={SECONDARY_BUTTON}
              onClick={() => setDecommissioning(d)}
            >
              {t('inventory.devices.decommission')}
            </button>
          ) : null}
          <button type="button" className={SECONDARY_BUTTON} onClick={() => setHistoryFor(d)}>
            {t('inventory.history')}
          </button>
        </span>
      ),
    },
  ];

  const chips: Array<RegisterChip<AdminDevice>> = [
    { key: 'all', label: t('inventory.chip.all'), predicate: () => true },
    { key: 'installed', label: t('inventory.chip.installed'), predicate: (d) => !d.inStock },
    { key: 'stock', label: t('inventory.chip.stock'), predicate: (d) => d.inStock },
    { key: 'noSim', label: t('inventory.chip.noSim'), predicate: (d) => d.simId === null },
    {
      key: 'attention',
      label: t('inventory.chip.attention'),
      predicate: (d) => d.status !== 'in_service',
    },
  ];

  return (
    <RegisterScreen
      headingId="devices-heading"
      title={t('inventory.devices.title')}
      headline={t('inventory.devices.headline', { installed, stock: inStock })}
      headerAction={
        <button type="button" className={PRIMARY_BUTTON} onClick={() => setCreating(true)}>
          {t('inventory.devices.new')}
        </button>
      }
      rows={rows}
      columns={columns}
      chips={chips}
      searchLabel={t('inventory.devices.search')}
      matches={(d, needle) =>
        [d.imei, d.model, d.firmware, d.vehiclePlate, d.simIccid, d.simMsisdn, d.notes]
          .filter((v): v is string => typeof v === 'string')
          .some((v) => v.toLowerCase().includes(needle))
      }
      emptyText={t('inventory.devices.empty')}
      noMatchText={(q) => t('inventory.devices.searchNone', { q })}
    >
      {creating ? <NewDeviceDialog onClose={() => setCreating(false)} /> : null}
      {editing ? <EditDeviceDialog device={editing} onClose={() => setEditing(null)} /> : null}
      {simFor ? (
        <DeviceSimDialog device={simFor} sims={sims} onClose={() => setSimFor(null)} />
      ) : null}
      {swapFor ? (
        <SwapDeviceDialog device={swapFor} candidates={rows} onClose={() => setSwapFor(null)} />
      ) : null}
      {historyFor ? (
        <DeviceHistoryDialog device={historyFor} onClose={() => setHistoryFor(null)} />
      ) : null}
      {commissioning ? (
        <CommissionDialog device={commissioning} onClose={() => setCommissioning(null)} />
      ) : null}
      {decommissioning ? (
        <DecommissionDeviceDialog
          device={decommissioning}
          onClose={() => setDecommissioning(null)}
        />
      ) : null}
      {assigning ? (
        <AssignDeviceDialog
          device={{
            deviceId: assigning.id,
            imei: assigning.imei,
            model: assigning.model,
            simIccid: assigning.simIccid,
            simMsisdn: assigning.simMsisdn,
            simCarrier: assigning.simCarrier,
          }}
          onClose={() => setAssigning(null)}
        />
      ) : null}
      {interlockFor ? (
        <SetInterlockDialog
          device={{ deviceId: interlockFor.id, imei: interlockFor.imei }}
          current={interlockFor.interlockType}
          onClose={() => setInterlockFor(null)}
        />
      ) : null}
    </RegisterScreen>
  );
}

export default Devices;
